import React, { useState, useCallback } from 'react';
import { View, TouchableOpacity, Text, StyleSheet } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout, AdminSelectDropdown } from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThreadModeration } from '@/hooks/admin/useThreadModeration';
import { ThreadModerationList } from '@/components/admin/corkBoard/ThreadModerationList';
import { EditThreadTagsModal } from '@/components/admin/corkBoard/EditThreadTagsModal';
import { AdminPostFormModal } from '@/components/admin/corkBoard/AdminPostFormModal';
import { GlassFormInput } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { CORK_BOARD_SORT_OPTIONS } from '@/constants/residents/corkBoardConstants';
import { showConfirm } from '@/utils/common/alert';
import { DesktopThreadModerationView } from './desktop/DesktopThreadModerationView';
import type { CorkBoardThreadSummary, ThreadSortBy } from '@/types/residents/corkBoard';

type ThreadModerationScreenProps = StackScreenProps<AdminStackParamList, 'ThreadModeration'>;

export const ThreadModerationScreen: React.FC<ThreadModerationScreenProps> = ({ navigation }) => {
  const { layoutMode } = useResponsive();
  const moderation = useThreadModeration({ kind: 'thread' });
  const { redesign, semantic } = useAdminColors();

  const [editTagsVisible, setEditTagsVisible] = useState(false);
  const [editingThread, setEditingThread] = useState<CorkBoardThreadSummary | null>(null);
  const [postModalVisible, setPostModalVisible] = useState(false);

  const handleOpenEditTags = useCallback((thread: CorkBoardThreadSummary) => {
    setEditingThread(thread);
    setEditTagsVisible(true);
  }, []);

  const handleCloseEditTags = useCallback(() => {
    setEditTagsVisible(false);
    setEditingThread(null);
  }, []);

  const handleSaveTags = useCallback(
    async (threadId: string, tagIds: number[]) => {
      const success = await moderation.handleUpdateThreadTags(threadId, tagIds);
      if (success) handleCloseEditTags();
    },
    [moderation, handleCloseEditTags],
  );

  const handleOpenPostModal = useCallback(() => {
    setPostModalVisible(true);
  }, []);

  const handleClosePostModal = useCallback(() => {
    setPostModalVisible(false);
  }, []);

  const handleSaveAnnouncement = useCallback(
    async (data: { title: string; body: string; tagIds: number[] }) => {
      const success = await moderation.handleCreateAnnouncement(data);
      if (success) handleClosePostModal();
    },
    [moderation, handleClosePostModal],
  );

  const handleViewResponses = useCallback(
    (thread: CorkBoardThreadSummary) => {
      navigation.navigate('SurveyResponses', { threadId: thread.id });
    },
    [navigation],
  );

  const handleDelete = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      const confirmed = await showConfirm(
        'Delete Thread',
        `Are you sure you want to delete "${thread.title}"? This action cannot be undone.`,
        'Delete',
        'Cancel',
      );
      if (confirmed) {
        await moderation.handleDeleteThread(thread.id);
      }
    },
    [moderation],
  );

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="ThreadModerationDesktop">
        <DesktopThreadModerationView
          moderation={moderation}
          editTagsVisible={editTagsVisible}
          editingThread={editingThread}
          postModalVisible={postModalVisible}
          onOpenEditTags={handleOpenEditTags}
          onCloseEditTags={handleCloseEditTags}
          onSaveTags={handleSaveTags}
          onOpenPostModal={handleOpenPostModal}
          onClosePostModal={handleClosePostModal}
          onSaveAnnouncement={handleSaveAnnouncement}
          onDelete={handleDelete}
          onViewResponses={handleViewResponses}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="ThreadModeration">
      <BaseAdminScreen
        screenName="ThreadModeration"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Threads"
          emptyStateType="corkBoardThreads"
          onAddPress={handleOpenPostModal}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
          disableScrollView={true}
        >
          <View style={[styles.searchContainer, { borderBottomColor: redesign.card.border }]}>
            <GlassFormInput
              value={moderation.searchInput}
              onChangeText={moderation.setSearchInput}
              placeholder="Search threads..."
            />
          </View>
          <AdminSelectDropdown
            label="Sort"
            options={CORK_BOARD_SORT_OPTIONS}
            selectedValue={moderation.sortBy}
            onSelect={(value) => moderation.setSortBy(value as ThreadSortBy)}
          />

          {moderation.error && (
            <View
              style={[
                styles.errorBanner,
                {
                  backgroundColor: redesign.accent.redBg,
                  borderBottomColor: semantic.status.error,
                },
              ]}
            >
              <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
                Failed to load threads.
              </Text>
              <TouchableOpacity onPress={moderation.refetch}>
                <Text style={[styles.errorRetryText, { color: semantic.status.error }]}>
                  Retry
                </Text>
              </TouchableOpacity>
            </View>
          )}

          <ThreadModerationList
            threads={moderation.threads}
            isLoading={moderation.isLoading}
            isMutating={moderation.isMutating}
            onPin={moderation.handlePinThread}
            onLock={moderation.handleLockThread}
            onEditTags={handleOpenEditTags}
            onDelete={handleDelete}
            onViewResponses={handleViewResponses}
            onRefresh={moderation.refetch}
            isRefreshing={moderation.isManualRefreshing}
            onLoadMore={moderation.loadMore}
            hasNextPage={moderation.hasNextPage}
            isFetchingMore={moderation.isFetchingNextPage}
          />
        </AdminEmptyScreenLayout>

        <EditThreadTagsModal
          visible={editTagsVisible}
          thread={editingThread}
          availableTags={moderation.tags}
          isSaving={moderation.isUpdatingTags}
          onSave={handleSaveTags}
          onClose={handleCloseEditTags}
        />
        <AdminPostFormModal
          visible={postModalVisible}
          kind="announcement"
          availableTags={moderation.tags}
          isSavingAnnouncement={moderation.isCreatingAnnouncement}
          onSaveAnnouncement={handleSaveAnnouncement}
          onClose={handleClosePostModal}
        />

      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  searchContainer: {
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  errorBannerText: {
    fontSize: 13,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
